/* eslint-disable react/no-unstable-nested-components */
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  useGetCoveragesQuery,
  useMergeCoveragesMutation,
} from '@/services/api/coverageService';

import { CoverageContentWrapper } from '../shared/CoverageContentWrapper';
import { CoverageErrorState } from '../shared/CoverageErrorState';
import { MergePageHeader } from './MergePageHeader';
import { MergeStepPreview } from './MergeStepPreview';
import { MergeStepSelection } from './MergeStepSelection';

export const MergeCoveragePage = () => {
  const navigate = useNavigate();
  const { matter_id } = useParams<{ matter_id: string }>();
  const [searchParams] = useSearchParams();
  const [destinationId, setDestinationId] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<'select' | 'preview'>(
    'select',
  );

  const coverageIds =
    searchParams
      .get('ids')
      ?.split(',')
      .filter((id: string) => id.trim()) || [];

  const {
    data: allCoverages,
    isLoading: isLoadingCoverages,
    error: coveragesError,
  } = useGetCoveragesQuery({ matterId: matter_id! }, { skip: !matter_id });

  const [mergeCoverages, { isLoading }] = useMergeCoveragesMutation();

  // Filter to only the coverages we want to merge
  const coverages =
    allCoverages?.filter((c: any) => coverageIds.includes(c.id)) || [];

  useEffect(() => {
    // Redirect immediately if there's no matter_id
    if (!matter_id) {
      navigate('/matters');
      return;
    }

    // Only check for sufficient coverages after the data has loaded
    if (!isLoadingCoverages && allCoverages) {
      if (coverageIds.length < 2 || coverages.length < 2) {
        navigate(`/matters/${matter_id}/coverage`);
      }
    }
  }, [
    matter_id,
    coverageIds.length,
    coverages.length,
    allCoverages,
    isLoadingCoverages,
    navigate,
  ]);

  // Redirect if no matter_id
  if (!matter_id) {
    return null;
  }

  // Show loading state while coverages are being fetched
  if (isLoadingCoverages) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Loading Coverage Data...
          </h1>
          <p className="text-gray-600">
            Please wait while we fetch the coverage information.
          </p>
        </div>
      </div>
    );
  }

  // Show error state if coverages failed to load
  if (coveragesError) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="space-y-4">
          <CoverageErrorState
            onRetry={() => window.location.reload()}
            message={`Unable to load coverage information for matter: ${matter_id}`}
          />
          <Button onClick={() => navigate(`/matters/${matter_id}/coverage`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Coverage List
          </Button>
        </div>
      </div>
    );
  }

  // Show invalid selection state
  if (coverages.length < 2) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Invalid Coverage Selection
          </h1>
          <p className="text-gray-600 mb-4">
            At least 2 coverages are required to perform a merge.
          </p>
          <Button onClick={() => navigate(`/matters/${matter_id}/coverage`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Coverage List
          </Button>
        </div>
      </div>
    );
  }

  const handleMerge = async () => {
    if (!destinationId) {
      toast.error('Please select a destination coverage');
      return;
    }

    const sourceIds = coverages
      .filter((c: any) => c.id !== destinationId)
      .map((c: any) => c.id);

    try {
      await mergeCoverages({
        matterId: matter_id!,
        mergeCoverageRequest: {
          dst_coverage_id: destinationId,
          src_coverage_ids: sourceIds,
        },
      }).unwrap();

      toast.success(
        `Successfully merged ${sourceIds.length} coverage${sourceIds.length === 1 ? '' : 's'}`,
      );
      navigate(`/matters/${matter_id}/coverage`);
    } catch (error) {
      toast.error('Failed to merge coverages');
    }
  };

  const handleCancel = () => {
    navigate(`/matters/${matter_id}/coverage`);
  };

  const handleNext = () => {
    if (!destinationId) {
      toast.error('Please select a destination coverage');
      return;
    }
    setCurrentStep('preview');
  };

  const handleBack = () => {
    setCurrentStep('select');
  };

  const destinationCoverage = coverages.find(
    (c: any) => c.id === destinationId,
  );
  const sourceCoverages = coverages.filter((c: any) => c.id !== destinationId);

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <MergePageHeader
        currentStep={currentStep}
        destinationId={destinationId}
        isLoading={isLoading}
        onCancel={handleCancel}
        onBack={handleBack}
        onNext={handleNext}
        onMerge={handleMerge}
      />

      <CoverageContentWrapper>
        {currentStep === 'select' ? (
          <MergeStepSelection
            coverages={coverages}
            destinationId={destinationId}
            onDestinationChange={setDestinationId}
          />
        ) : (
          destinationCoverage && (
            <MergeStepPreview
              destinationCoverage={destinationCoverage}
              sourceCoverages={sourceCoverages}
            />
          )
        )}
      </CoverageContentWrapper>
    </div>
  );
};
